/**
 * A file to manage permissions to help reduce testing strain for various scenarios.
 *
 * isOrgMember - determines if the user is an org member. True if org member, false if org owner.
 * isBookkeeper - determines if a user is a bookkeeper.
 * hasProTools - determines if a user is paying for a subscription, in trial, or has promo.
 *
 */
export const derivePermissions = ({
  id,
  email,
  username,
  firstName,
  lastName,
  profileImage,
  organization,
  organizationMember,
  admin,
  isDisabled,
  features,
  loginWeddings,
  preferences,
  isPro,
  isPaused,
  integrationsEnabled,
  integrations,
  billing,
  /* eslint-disable-next-line no-unused-vars */
  ...restProps
}) => {
  const { effectivePermissions } = organizationMember || {};
  const { planningSubscription } = billing || {};
  const { isTrial } = planningSubscription || {};
  // Permission simplifying.
  const isInOrg = !!organization;
  const isOrgOwner = isInOrg && organization?.ownerId === id;
  const isOrgMember = !isOrgOwner && isInOrg;
  const isBookkeeper = isOrgMember && effectivePermissions?.is_bookeeper;
  // This doesn't fully capture the client/vendor persona. A user can be in an org, but
  // is invited to a project, in which they are a vendor in the context of that project,
  // but is still in an org normally. But this is to mostly say that I shouldn't
  // be able to see the billing page, go through onboarding, and how to handle routing.
  // Should maybe consider renaming this to be more appropriate.
  const isClientVendor = !isOrgOwner && !isOrgMember;
  const isPlanningPartner = isClientVendor;
  const isPaying = !!organization?.isPaying;
  const isFreeAccount = isInOrg && !isPaying;
  const isInTrial = isTrial;
  const isSubscribed = isInOrg && organization.isPaying > 0;
  const hasUnpausedProjects = (isPaused || isSubscribed) ||
    loginWeddings?.items?.length > 0;

  const integratedStatuses = {
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    DISABLED: 'disabled',
  };

  const integratedStatus =
    integrations.some(integration => integration.grantStatus === 'valid') ?
      integratedStatuses.CONNECTED :
    integrationsEnabled ?
      integratedStatuses.DISCONNECTED :
      integratedStatuses.DISABLED;

  const personas = {
    isInOrg,
    isOrgOwner,
    isOrgMember,
    isBookkeeper,
    isClientVendor,
    isPlanningPartner,
    isPaying,
    isFreeAccount,
    isInTrial,
    isSubscribed,
    isPaused,
    isPro,
    hasUnpausedProjects,
    integratedStatus,
  };

  const availableIntegrationSeats = 1;

  // Nylas
  // We don't have organizations integrating more accounts than they pay for.
  const hasIntegrationSeat = integrations.length < availableIntegrationSeats;
  // Can only send emails if account is nylas enabled and has an integration
  const canSendEmail = integrations.length > 0;
  const derivedPermissions = {
    hasIntegrationSeat,
    canSendEmail,
  };

  // Menu permissions
  const menuPermissions = {
    orgSettings: {
      // Most org settings need full access for the respective area and must have an active account.
      business: !isDisabled && effectivePermissions && effectivePermissions['settings.org.full-access'],
      banking: !isDisabled && effectivePermissions &&  effectivePermissions['settings.org.full-access'],
      // Non-active pro users should still be able to manage their users.
      users: !isDisabled && effectivePermissions && effectivePermissions['settings.org.full-access'],
      branding: !isDisabled && effectivePermissions && effectivePermissions['settings.branding.full-access'],
      leads: !isDisabled && effectivePermissions && effectivePermissions['settings.lead.full-access'],
      templates: !isDisabled && effectivePermissions && effectivePermissions['settings.template.full-access'],
      integrations: !isDisabled && (
        (effectivePermissions && effectivePermissions['settings.org.full-access']) || integrationsEnabled
      ),
    },
  };

  return {
    personas,
    derivedPermissions,
    menuPermissions,
  };
};
